import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/Skeleton';
import { Button } from '@/components/Button';
import { Avatar, displayName } from '@/components/Avatar';
import { CardCounters } from '@/features/card/CardCounters';
import { EmptyShelf } from './EmptyShelf';
import { api } from '@/api/endpoints';
import { useAuth } from '@/store/auth';
import { useUI } from '@/store/ui';
import { shareCard } from '@/lib/shareCard';
import { ENV } from '@/lib/env';
import type { ContentCard, FriendShelfItem } from '@/api/types';

function toContentCard(item: FriendShelfItem): ContentCard {
  return {
    id: item.id,
    type: 'insight',
    topic_id: '',
    text: item.text,
    book: { id: item.book_id, title: item.book_title, author: null, year: null, purchase_url: null },
    stats: item.stats,
    user_actions: item.user_actions,
  };
}

export function ShelfFriends() {
  const { t } = useTranslation();
  const showToast = useUI(s => s.showToast);
  const myRefCode = useAuth(s => s.user?.ref_code ?? null);
  const [items, setItems] = useState<FriendShelfItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.shelf.friends(1)
      .then(({ data }) => {
        setItems(data);
        setHasMore(data.length === 20);
      })
      .catch(() => showToast(t('errors.network'), 'error'))
      .finally(() => setLoading(false));
  }, [t, showToast]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const { data } = await api.shelf.friends(next);
      setItems(prev => [...prev, ...data]);
      setPage(next);
      setHasMore(data.length === 20);
    } catch {
      showToast(t('errors.network'), 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const onAction = async (item: FriendShelfItem, action: 'like' | 'dislike' | 'bookmark' | 'share') => {
    // Snapshot for rollback
    const snapUA = { ...item.user_actions };
    const snapStats = { ...item.stats };

    // Optimistic update
    setItems(prev => prev.map(it => {
      if (it.id !== item.id || it.saved_by.id !== item.saved_by.id) return it;
      const ua = { ...it.user_actions };
      const st = { ...it.stats };
      if (action === 'like') {
        const next = !ua.liked;
        ua.liked = next;
        st.likes += next ? 1 : -1;
        if (ua.disliked && next) { ua.disliked = false; st.dislikes -= 1; }
      } else if (action === 'dislike') {
        const next = !ua.disliked;
        ua.disliked = next;
        st.dislikes += next ? 1 : -1;
        if (ua.liked && next) { ua.liked = false; st.likes -= 1; }
      } else if (action === 'bookmark') {
        const next = !ua.bookmarked;
        ua.bookmarked = next;
        st.bookmarks += next ? 1 : -1;
      } else if (action === 'share') {
        st.shares += 1;
      }
      return { ...it, user_actions: ua, stats: st };
    }));

    if (action === 'share') {
      const startParam = `card_${item.id}${myRefCode ? `__ref_${myRefCode}` : ''}`;
      const url = `https://t.me/${ENV.BOT_USERNAME}?startapp=${startParam}`;
      const text = t('feed.share_text', {
        quote: item.text ?? '',
        author: '',
        title: item.book_title ?? '',
        url,
      });
      void shareCard(item.id, { url, text });
    }

    try {
      await api.cards.toggle(item.id, action);
    } catch {
      // Rollback
      setItems(prev => prev.map(it => {
        if (it.id !== item.id || it.saved_by.id !== item.saved_by.id) return it;
        return { ...it, user_actions: snapUA, stats: snapStats };
      }));
      showToast(t('errors.generic'), 'error');
    }
  };

  if (loading) {
    return <div className="space-y-3"><Skeleton height={120} /><Skeleton height={120} /></div>;
  }

  if (items.length === 0) {
    return <EmptyShelf title={t('shelf.friends_empty')} />;
  }

  return (
    <div className="space-y-3">
      {items.map(item => (
        <div key={`${item.id}-${item.saved_by.id}`} className="rounded-xl bg-surface overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-3 pb-1">
            <Avatar user={item.saved_by} size="xs" />
            <span className="text-xs text-text-muted truncate">
              {displayName(item.saved_by, t('common.friend_fallback'))}
            </span>
          </div>
          <p className="font-content text-base leading-snug px-4 py-2">{item.text ?? '—'}</p>
          {item.book_title && (
            <p className="text-xs text-text-muted italic px-4 pb-2">{item.book_title}</p>
          )}
          <div className="pb-3">
            <CardCounters
              card={toContentCard(item)}
              onLike={() => onAction(item, 'like')}
              onDislike={() => onAction(item, 'dislike')}
              onBookmark={() => onAction(item, 'bookmark')}
              onShare={() => onAction(item, 'share')}
              onComments={() => {}}
            />
          </div>
        </div>
      ))}
      {hasMore && (
        <Button block variant="secondary" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? t('common.loading_more') : t('common.load_more')}
        </Button>
      )}
    </div>
  );
}
